import React, { useState, useEffect, useRef, useMemo } from "react";
import { QRCodeCanvas } from "qrcode.react";
import axios from "axios";
import Cookies from "js-cookie";
import copy from "copy-to-clipboard";
import { BASE_URL } from "../api";
import MessageToster from "./MessageToster";
import { getApiHeaders } from "../utils/helpers";
import { useUser } from "../context/UserContext";

/**
 * Formats or transforms the TOTP URI so that the authenticator app displays
 * the user's login ID (e.g. CRM490313) instead of internal DB user ID (e.g. 28).
 */
export const getFormattedTotpUri = (rawLink, secret, loginId) => {
    if (!loginId) {
        return rawLink || (secret ? `otpauth://totp/crm?secret=${secret}&issuer=crm` : "otpauth://totp/HopeflyCRM?secret=PLACEHOLDER");
    }

    const cleanLoginId = String(loginId).trim();

    if (rawLink) {
        try {
            const sanitized = rawLink.replace(/ /g, "%20");
            const url = new URL(sanitized);

            const secretVal = url.searchParams.get("secret") || secret || "";
            const decodedPath = decodeURIComponent(url.pathname).replace(/^\/+/, "");

            let issuerPrefix = "crm";
            if (decodedPath.includes(":")) {
                issuerPrefix = decodedPath.split(":")[0].trim() || "crm";
            }

            const newLabel = `${issuerPrefix}: ${cleanLoginId}`;
            url.pathname = `/${newLabel}`;

            if (!url.searchParams.get("issuer")) {
                url.searchParams.set("issuer", issuerPrefix);
            }
            if (secretVal && !url.searchParams.get("secret")) {
                url.searchParams.set("secret", secretVal);
            }

            return url.toString();
        } catch (e) {
            let modified = rawLink;
            if (/crm:(\s*|%20*)\d+/i.test(modified)) {
                return modified.replace(/crm:(\s*|%20*)\d+/i, `crm: ${cleanLoginId}`);
            }
            if (secret) {
                return `otpauth://totp/crm:%20${encodeURIComponent(cleanLoginId)}?secret=${secret}&issuer=crm`;
            }
            return rawLink;
        }
    }

    if (secret) {
        return `otpauth://totp/crm:%20${encodeURIComponent(cleanLoginId)}?secret=${secret}&issuer=crm`;
    }

    return "otpauth://totp/HopeflyCRM?secret=PLACEHOLDER";
};

/**
 * TwoFactorModal
 * @param {Object} qrData - The QR data object containing qr_link and privatekey
 * @param {boolean} loading - Boolean flag indicating if QR data is loading
 * @param {Function} onSuccess - Callback triggered after successful 2FA setup
 * @param {string} loginId - Optional login ID of the user (e.g. CRM490313)
 */
export default function TwoFactorModal({ qrData = null, loading = false, onSuccess, loginId = "" }) {
    const { userData } = useUser() || {};
    const [step, setStep] = useState(1);
    const [code, setCode] = useState(["", "", "", "", "", ""]);
    const codeRefs = useRef([]);
    const [submitLoading, setSubmitLoading] = useState(false);
    const [copied, setCopied] = useState(false);

    // Resolve user login id from prop, userContext, cookies or sessionStorage
    const effectiveLoginId = useMemo(() => {
        if (loginId) return loginId;
        if (userData?.loginid) return userData.loginid;
        try {
            const cookieUser = JSON.parse(Cookies.get("userdata") || "{}");
            if (cookieUser?.loginid) return cookieUser.loginid;
        } catch (e) { }
        return sessionStorage.getItem("loginid") || "";
    }, [loginId, userData]);

    const effectiveQrLink = useMemo(() => {
        return getFormattedTotpUri(qrData?.qr_link, qrData?.privatekey, effectiveLoginId);
    }, [qrData?.qr_link, qrData?.privatekey, effectiveLoginId]);

    const handleCopy = () => {
        const textToCopy = qrData?.privatekey;
        if (!textToCopy) return;

        let success = false;
        try {
            success = copy(textToCopy);
        } catch (e) {
            console.error("copy-to-clipboard error:", e);
        }

        if (!success && typeof navigator !== "undefined" && navigator?.clipboard?.writeText) {
            navigator.clipboard.writeText(textToCopy).catch(() => { });
        }

        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
    };

    const handleInput = (e, idx) => {
        const val = e.target.value.replace(/\D/g, "").slice(-1);
        const newCode = [...code];
        newCode[idx] = val;
        setCode(newCode);
        if (val && idx < 5) {
            codeRefs.current[idx + 1]?.focus();
        }
    };

    const handleKeyDown = (e, idx) => {
        if (e.key === "Backspace" && !code[idx] && idx > 0) {
            codeRefs.current[idx - 1]?.focus();
        }
    };

    const handlePaste = (e) => {
        e.preventDefault();
        const pasteData = e.clipboardData.getData("text").replace(/\D/g, "").slice(0, 6);
        if (pasteData) {
            const newCode = [...code];
            for (let i = 0; i < pasteData.length; i++) {
                if (i < 6) newCode[i] = pasteData[i];
            }
            setCode(newCode);
            const nextEmpty = newCode.findIndex((v) => !v);
            codeRefs.current[nextEmpty === -1 ? 5 : nextEmpty]?.focus();
        }
    };

    useEffect(() => {
        const modalElement = document.getElementById("twoFactorModal");

        const handleHidden = () => {
            setStep(1);
            setCode(["", "", "", "", "", ""]);
            setSubmitLoading(false);
        };

        if (modalElement) {
            modalElement.addEventListener("hidden.bs.modal", handleHidden);
        }
        return () => {
            if (modalElement) {
                modalElement.removeEventListener("hidden.bs.modal", handleHidden);
            }
        };
    }, []);

    const handleSubmitCode = async () => {
        const finalCode = code.join("");
        if (finalCode.length < 6) {
            MessageToster("error", "Please enter a valid 6-digit code.");
            return;
        }
        if (submitLoading) return;

        setSubmitLoading(true);
        try {
            const res = await axios.post(
                `${BASE_URL}/account/verify_otp_2fa`,
                {
                    enable: true,
                    otp: finalCode,
                },
                { headers: getApiHeaders() }
            );

            if (res.data.code === 200) {
                MessageToster("success", res.data.message || "2FA enabled successfully!");
                if (onSuccess) onSuccess();

                const modalEl = document.getElementById("twoFactorModal");
                if (modalEl && window.bootstrap?.Modal) {
                    window.bootstrap.Modal.getOrCreateInstance(modalEl).hide();
                }
            } else {
                MessageToster("error", res.data.message || "Invalid OTP. Please try again.");
            }
        } catch (err) {
            MessageToster("error", err.message || "Something went wrong.");
        } finally {
            setSubmitLoading(false);
        }
    };

    return (
        <>
            <div className="modal fade" id="twoFactorModal" tabIndex="-1" aria-labelledby="twoFactorModalLabel" aria-hidden="true">
                <div className="modal-dialog modal-dialog-centered">
                    <div className="modal-content">

                        {/* Header */}
                        <div className="modal-header">
                            <h5 className="modal-title" id="twoFactorModalLabel">
                                {step === 1 ? "SET UP 2FA" : "TWO-FACTOR VERIFICATION"}
                            </h5>
                            <button
                                type="button"
                                className="btn-close"
                                data-bs-dismiss="modal"
                                aria-label="Close"
                            />
                        </div>

                        {/* Body */}
                        <div className="modal-body p-4 text-center">

                            {/* ── Step 1: Scan QR ── */}
                            {step === 1 && (
                                <div>
                                    <div className="row align-items-center justify-content-center mb-4">

                                        {/* QR Code */}
                                        <div className="col-md-4 text-center border-end">
                                            {loading ? (
                                                <div
                                                    className="d-inline-flex align-items-center justify-content-center bg-light border border-primary border-1"
                                                    style={{ width: 158, height: 158, borderRadius: 8 }}
                                                >
                                                    <div className="spinner-border text-primary" role="status">
                                                        <span className="visually-hidden">Loading...</span>
                                                    </div>
                                                </div>
                                            ) : (
                                                <div
                                                    className="d-inline-block p-1 border border-primary border-1 bg-white"
                                                    style={{ borderRadius: "8px" }}
                                                >
                                                    <QRCodeCanvas
                                                        value={effectiveQrLink}
                                                        size={150}
                                                        level="M"
                                                    />
                                                </div>
                                            )}
                                        </div>

                                        {/* Manual Secret Key */}
                                        <div className="col-md-8 text-center px-4">
                                            {effectiveLoginId && (
                                                <div className="mb-2">
                                                    <span className="badge bg-light text-dark border px-2 py-1" style={{ fontSize: "12px", fontWeight: "600" }}>
                                                        Account: <span className="text-primary">{effectiveLoginId}</span>
                                                    </span>
                                                </div>
                                            )}
                                            <p className="text-muted small mb-2">Or enter the following code manually</p>
                                            {loading ? (
                                                <div className="placeholder-glow">
                                                    <span className="placeholder col-12 rounded" style={{ height: 38 }} />
                                                </div>
                                            ) : (
                                                <>
                                                    <input
                                                        type="text"
                                                        className="form-control text-center border-primary border-1 bg-light fw-bold"
                                                        value={qrData?.privatekey || ""}
                                                        readOnly
                                                        style={{ letterSpacing: "1px", borderRadius: "8px" }}
                                                    />
                                                    <button
                                                        className="btn btn-link text-primary mt-2 fw-bold text-decoration-none p-0"
                                                        onClick={handleCopy}
                                                        style={{ boxShadow: "none", outline: "none" }}
                                                    >
                                                        {copied ? <><i className="bx bx-check-double me-1"></i>Copied!</> : <><i className="bx bx-copy me-1"></i>Copy</>}
                                                    </button>
                                                </>
                                            )}
                                        </div>
                                    </div>

                                    {/* ── Instructions ── */}
                                    <div className="text-start border-top pt-3 mb-3">
                                        <ol className="text-muted mb-0 ps-3" style={{ fontSize: "13.5px", lineHeight: "1.8" }}>
                                            <li>Install <strong className="text-dark">Google Authenticator</strong> or <strong className="text-dark">Authy</strong> on your phone.</li>
                                            <li>Open the app and tap <strong className="text-dark">Add Account</strong> → <strong className="text-dark">Scan QR Code</strong>.</li>
                                            <li>After scanning, click <strong className="text-dark">Next</strong> and enter the 6-digit code to verify.</li>
                                        </ol>
                                    </div>

                                    {/* Next button */}
                                    <div className="d-flex justify-content-end align-items-center border-top pt-3">
                                        <button
                                            className="btn btn-primary rounded-pill px-5 py-2 fw-bold shadow-sm"
                                            onClick={() => setStep(2)}
                                            disabled={loading || !effectiveQrLink}
                                        >
                                            Next
                                        </button>
                                    </div>
                                </div>
                            )}

                            {/* ── Step 2: Enter OTP + API call ── */}
                            {step === 2 && (
                                <div className="py-2 px-sm-3">
                                    <p className="text-dark fw-medium mb-4" style={{ fontSize: "15px" }}>
                                        Enter the 6-digit code from your<br />authenticator app to complete sign-in.
                                    </p>
                                    <div
                                        className="d-flex justify-content-center gap-2 mb-4"
                                        onPaste={handlePaste}
                                    >
                                        {code.map((digit, idx) => (
                                            <input
                                                key={idx}
                                                ref={(el) => (codeRefs.current[idx] = el)}
                                                type="text"
                                                inputMode="numeric"
                                                maxLength={2}
                                                value={digit}
                                                onFocus={(e) => e.target.select()}
                                                onChange={(e) => handleInput(e, idx)}
                                                onKeyDown={(e) => handleKeyDown(e, idx)}
                                                disabled={submitLoading}
                                                autoComplete="one-time-code"
                                                className={`form-control text-center fw-bold p-0 otp-input-box${digit ? " border-primary" : ""}`}
                                                style={{
                                                    boxShadow: digit ? "0 0 0 0.15rem rgba(var(--bs-primary-rgb), 0.2)" : "none",
                                                }}
                                            />
                                        ))}
                                    </div>
                                    <button
                                        className="btn btn-primary w-100 btn-lg fw-bold"
                                        onClick={handleSubmitCode}
                                        disabled={submitLoading}
                                    >
                                        {submitLoading ? (
                                            <>
                                                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true" />
                                                Verifying...
                                            </>
                                        ) : (
                                            "Submit Code"
                                        )}
                                    </button>
                                </div>
                            )}

                        </div>
                    </div>
                </div>
            </div>
            <style>{`
                .otp-input-box {
                    width: 50px;
                    height: 55px;
                    font-size: 20px;
                    border-radius: 8px;
                    border-width: 1px;
                    transition: border-color 0.2s, box-shadow 0.2s;
                }
                @media (max-width: 420px) {
                    .otp-input-box {
                        width: 40px !important;
                        height: 48px !important;
                        font-size: 18px !important;
                    }
                }
                @media (max-width: 340px) {
                    .otp-input-box {
                        width: 35px !important;
                        height: 42px !important;
                        font-size: 16px !important;
                    }
                }
            `}</style>
        </>
    );
}
